import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Lock, LockSlash } from "iconoir-react";
import {
  ControlledAddressAutofill,
  ControlledInput,
  ControlledSelectRoot,
} from "shared/components/formControl";
import { Box, Flex } from "styled-system/jsx";
import { Button, Checkbox, Select, Text } from "@ttc3k/trekker";
import { AssetImage, EnumPeopleContactMethod } from "gql/generated";
import { useCommonContactInfo } from "./useCommonContactInfo";
import { DateInput } from "shared/components/atoms";
import { defaultHiddenEmailStart } from "shared/utils";
import { UploadAvatar } from "shared/components/molecules";

type CommonContactInfoProps = {
  isUserClaimed: boolean;
  isUserInvited: boolean;
  personPhoto?: Partial<AssetImage> | null;
};

export const CommonContactInfo = ({
  isUserClaimed,
  isUserInvited,
  personPhoto,
}: CommonContactInfoProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO.LABELS",
  });

  const [isEmailUnlocked, setIsEmailUnlocked] = useState(false);

  const {
    contactMethodTypes,
    contactMethod,
    email,
    minBirthday,
    maxBirthday,
    setValue,
    birthday,
    optInOptions,
  } = useCommonContactInfo();

  const collection = Select.createCollection({ items: contactMethodTypes });

  return (
    <Flex flexDir="column" gap="150">
      <UploadAvatar
        image={personPhoto ?? undefined}
        onUploadComplete={image => {
          if (image?._id) {
            setValue("photo", image._id, { shouldDirty: true });
          }
        }}
        onRemoveClick={() => {
          setValue("photo", "$unset", { shouldDirty: true });
        }}
      />
      <ControlledInput
        name="firstName"
        label={t("FIRST_NAME")}
        placeholder="John"
      />
      <ControlledInput
        name="lastName"
        label={t("LAST_NAME")}
        placeholder="Doe"
      />
      <ControlledInput name="pronouns" label={t("PRONOUNS")} />
      <ControlledInput name="primaryTitle" label={t("PRIMARY_TITLE")} />
      <DateInput
        title={t("BIRTHDAY")}
        value={birthday}
        onValueChange={value =>
          setValue("birthday", value, { shouldDirty: true })
        }
        min={minBirthday}
        max={maxBirthday}
        canSelectPastDates={true}
        allowYearView={true}
      />
      <ControlledSelectRoot
        name="contactMethod"
        label={t("PREFERRED_CONTACT_METHOD")}
        collection={collection}
        value={[contactMethod ?? ""]}
        onValueChange={e =>
          setValue("contactMethod", e.value[0] as EnumPeopleContactMethod, {
            shouldDirty: true,
          })
        }
        mb="50"
      >
        {collection.items.map(item => (
          <Select.Item key={item.value} item={item}>
            {item.label}
          </Select.Item>
        ))}
      </ControlledSelectRoot>

      {/* EMAIL */}
      {email?.startsWith(defaultHiddenEmailStart) ? (
        <Flex flexDir="column" gap="0">
          <Text visual="smallMedium" color="text.mid">
            {t("PRIMARY_EMAIL")}
          </Text>
          <Button
            mode="light"
            my="50"
            h="40px"
            onClick={() => {
              setValue("email", "", { shouldDirty: true });
            }}
            disabled={isUserClaimed}
          >
            {t("SET_EMAIL")}
          </Button>
          {(isUserClaimed || isUserInvited) && (
            <Text visual="smallRegular" color="text.light">
              {isUserClaimed
                ? t("PRIMARY_EMAIL_HINT")
                : t("PRIMARY_EMAIL_HINT_INVITED")}
            </Text>
          )}
        </Flex>
      ) : (
        <Flex direction="column" gap="50" w="full">
          <Flex alignItems="flex-end" gap="150" w="full">
            <Box flex="1">
              <ControlledInput
                name="email"
                label={t("PRIMARY_EMAIL")}
                disabled={isUserClaimed || (isUserInvited && !isEmailUnlocked)}
                w="full"
              />
            </Box>
            {isUserInvited && !isUserClaimed && (
              <Button
                mode="light"
                visual="unstyled"
                type="button"
                onClick={() => setIsEmailUnlocked(!isEmailUnlocked)}
                h="40px"
                px="100"
                border="1px solid"
                borderColor="border.light"
                borderRadius="100"
                alignSelf="flex-end"
                style={{ transform: "translateY(-3px)" }}
                _hover={{
                  bgColor: "bg.wa.lightest",
                }}
              >
                {isEmailUnlocked ? (
                  <LockSlash width="18px" />
                ) : (
                  <Lock width="18px" />
                )}
              </Button>
            )}
          </Flex>
          {(isUserClaimed || isUserInvited) && (
            <Text visual="smallRegular" color="text.light">
              {isUserClaimed
                ? t("PRIMARY_EMAIL_HINT")
                : t("PRIMARY_EMAIL_HINT_INVITED")}
            </Text>
          )}
        </Flex>
      )}

      {/* PHONE */}
      <Flex alignItems="flex-start" gap="150">
        <ControlledInput
          name="phone"
          label={t("PRIMARY_PHONE")}
          placeholder="555-444-3333"
          w="full"
        />
        <Box w="100px" minW="100px" maxW="100px">
          <ControlledInput
            name="phoneExt"
            label={t("PRIMARY_PHONE_EXT")}
            placeholder="123456"
          />
        </Box>
      </Flex>

      {/* FAX */}
      <Flex alignItems="flex-start" gap="150">
        <ControlledInput
          name="fax"
          label={t("PRIMARY_FAX")}
          placeholder="555-444-3333"
          w="full"
        />
        <Box w="100px" minW="100px" maxW="100px">
          <ControlledInput
            name="faxExt"
            label={t("PRIMARY_FAX_EXT")}
            placeholder="123456"
          />
        </Box>
      </Flex>

      {/* OPT IN */}
      <Flex flexDir="column" gap="50">
        <Text visual="smallMedium" color="text.mid">
          {t("OPT_IN.TITLE")}
        </Text>
        <Flex alignItems="center" flexWrap="wrap" gap="300" position="relative">
          {optInOptions.map(option => (
            <Checkbox
              key={option.name}
              name={option.name}
              label={option.label}
              checked={option.checked}
              onCheckedChange={e =>
                setValue(option.name, e.checked === true, {
                  shouldDirty: true,
                })
              }
              gap="150 !important"
              alignItems="center !important"
              _focusVisible={{
                outline: "1px solid",
                outlineColor: "border.light",
                outlineOffset: "2px",
                borderRadius: "25",
              }}
            />
          ))}
        </Flex>
      </Flex>

      <ControlledAddressAutofill
        addressLabel={t("PRIMARY_ADDRESS")}
        addressInputName={`address.fullAddress`}
        addressObjInputName={`address.address`}
        coordsInputName={`address.address.location.coordinates`}
        lngInputName={`address.address.location.coordinates.lng`}
        latInputName={`address.address.location.coordinates.lat`}
        displayAddressOnly
      />

      <ControlledAddressAutofill
        addressLabel={t("MAILING_ADDRESS")}
        addressInputName={`mailingAddress.fullAddress`}
        addressObjInputName={`mailingAddress.address`}
        coordsInputName={`mailingAddress.address.location.coordinates`}
        lngInputName={`mailingAddress.address.location.coordinates.lng`}
        latInputName={`mailingAddress.address.location.coordinates.lat`}
        displayAddressOnly
      />
    </Flex>
  );
};
